import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardMedia, Typography, Box, Chip, Tooltip, useTheme, useMediaQuery, Button } from '@mui/material';
import { Verified as VerifiedIcon, Star as StarIcon } from '@mui/icons-material';
import { getCachedImage } from '../utils/imageCache';
import './FeaturedProductCard.css';
import { UNIT_LABELS_SINGULAR, UNIT_LABELS_PLURAL } from '../utils/units';
import PriceDisplay from '../components/PriceDisplay';
import LdPlaceHolder from '../assets/ldplaceholder.png';
const FeaturedProductCard = ({ product }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const lastPriceBreak = product.priceBreaks?.[product.priceBreaks.length - 1];
  const formattedPrice = new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(product.priceBreaks?.length > 0 ? lastPriceBreak?.price : product.basePrice);

  return isMobile ? (
    <Card
      onClick={() => navigate(`/product/${product._id}`)}
      className="featured-card"
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection:'row' ,
        height: '260px',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        padding: 0,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'background.paper',
        borderBottom: { xs: '1px solid rgba(0,0,0,0.2)', sm: 'none' },
        borderTop: { xs: '1px solid rgba(0,0,0,0.2)', sm: 'none' },
        borderRadius: '2px',
        mb:0
      }}
    >
      {/* Featured Badge */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          backgroundColor: 'rgba(255, 124, 23)',
          color: 'white',
          padding: '2px 15px',
          zIndex: 2,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Typography variant="caption" sx={{ fontSize: '1rem' ,fontWeight: 700}}>
          Promocionado
        </Typography>
      </Box>

      {/* Image Container */}
      <Box
        sx={{
          position: 'relative',
          width: '40%' ,
          height: '100%',
          overflow: 'hidden',
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
          borderRadius: '2px',
        }}
      >
        
        
        <CardMedia
          component="img"
          image={getCachedImage(product.imageUrls?.[0]?.replace('upload/', 'upload/q_auto/')) || LdPlaceHolder}
          alt={product.name}
          className="product-image"
          sx={{
            height: '100%',
            width: '100%',
            objectFit: 'contain',
            transition: 'transform 0.3s ease',
          }}
        />
        <Box className="glow-effect" />
      </Box>

      {/* Content */}
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'normal',
          padding: 2,
          position: 'relative',
        }}
      >
        

        {/* Title and Description */}
        <Box>
          <Typography
            variant="h5"
            sx={{
              mb: 0.5,
              maxWidth: '100%',
              fontSize: '0.9rem',
              fontWeight: 500,
              color: 'text.primary',
            }}
          >
            {product.name}
          </Typography>
          <Box display="flex" gap={0.5} flexDirection="row" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
              {product.distributorName}
            </Typography>
            {product.distributorVerified && (
              <Tooltip title="Este distribuidor ha sido verificado por Liderdist." arrow placement="top">
                <Box sx={{
                  zIndex: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '50%',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '20px',
                  width: '20px',
                }}>
                  <VerifiedIcon sx={{ fontSize: '14px', color: '#2196F3' }} />
                </Box>
              </Tooltip>
            )}
          </Box>


          
        </Box>

        {/* Price and Category */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
        <Box flexDirection="column" alignItems="center">
        <Box display="flex" gap={0.5} flexDirection="row" alignItems="center">
          <Box display="flex" flexDirection="row" gap={0.5} alignItems="center">
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            {formattedPrice}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {product.priceBreaks?.length > 0 ? `por ${product.unit? product.unit : 'unidad'}` : ''}
          </Typography>
          </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {product.priceBreaks?.length > 0 
              ? `Desde ${lastPriceBreak?.minQuantity} ${UNIT_LABELS_PLURAL[product.unit]}`
              : 'Precio unitario'
            }
          </Typography>
          <PriceDisplay
              basePrice={product.basePrice}
              priceBreaks={product.priceBreaks}
              hidePrice={product.hidePrice}
              unit={product.unit}
              isMobile={isMobile}
              hasMainPrice={false}
            />
            </Box>
        </Box>
        
      </CardContent>
      {/* <Typography
          variant="caption"
            sx={{
              position: 'absolute',
              bottom: 10 ,
              left: 0,
              top:'auto',
              backgroundColor: 'rgba(255, 88, 51, 0.8)',
              px: 1.5,
              py: 0.5,
              fontWeight: 500,
              borderRadius: 0,
              borderTopRightRadius: 4,
              borderBottomRightRadius: 4,
              textOverflow: 'ellipsis',
              textShadow: '0 1px 3px rgba(0,0,0,0.4)',
              fontWeight: 500,
              letterSpacing: '0.3px',
              fontSize: '0.75rem',
              color: 'white',
              transition: 'transform 0.3s ease',
              animation: 'fadeIn 0.5s ease-in-out',
            }}
          >
            {product.category}
          </Typography> */}
    </Card>
  ) : (
    <Card
      onClick={() => navigate(`/product/${product._id}`)}
      className="featured-card"
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'row',
        height: '300px' ,
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'background.paper',
        borderBottom: { xs: '1px solid rgba(0,0,0,0.2)', sm: 'none' },
      }}
    >
      {/* Featured Badge */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          backgroundColor: 'rgba(255, 124, 23)',
          color: 'white',
          padding: '2px 15px',
          zIndex: 2,
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Typography variant="caption" sx={{ fontSize: '0.9rem' }}>
          Promocionado
        </Typography>
      </Box>

      {/* Image Container */}
      <Box
        sx={{
          position: 'relative',
          width: '40%' ,
          height: '100%',
          overflow: 'hidden',
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
        }}
      >
        {/* Gradient Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to left, rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0))',
            zIndex: 10,
            pointerEvents: 'none',
          }}
        />
        
        <CardMedia
          component="img"
          image={product.imageUrls?.[0] ? getCachedImage(product.imageUrls?.[0]) : LdPlaceHolder}
          alt={product.name}
          className="product-image"
          sx={{
            height: '100%',
            width: '100%',
            objectFit: 'contain',
            transition: 'transform 0.3s ease',
          }}
        />
        <Box className="glow-effect" />
      </Box>

      {/* Content */}
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'normal',
          padding: 2,
          position: 'relative',
        }}
      >
        

        {/* Title and Description */}
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              mb: 1,
              maxWidth: '80%',
              fontSize: '1.1rem',
              color: 'text.primary',
            }}
          >
            {product.name}
          </Typography>
          <Box display="flex" gap={0.5} flexDirection="row" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
              {product.distributorName}
            </Typography>
            {product.distributorVerified && (
              <Tooltip title="Este distribuidor ha sido verificado por Liderdist." arrow placement="top">
                <Box sx={{
                  zIndex: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '50%',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '20px',
                  width: '20px',
                }}>
                  <VerifiedIcon sx={{ fontSize: '14px', color: '#2196F3' }} />
                </Box>
              </Tooltip>
            )}
          </Box>


          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: isMobile ? 2 : 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {product.description}
          </Typography>
        </Box>

        {/* Price and Category */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt:1
          }}
        >
        <Box flexDirection="column" alignItems="center">
        <Box display="flex" gap={0.5} flexDirection="row" alignItems="center">
          <Box display="flex" flexDirection="row" gap={0.5} alignItems="center">
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            {formattedPrice}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            por {product.unit? product.unit : 'unidad'}
          </Typography>
          </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {product.priceBreaks?.length > 0 
              ? `Desde ${lastPriceBreak?.minQuantity} ${UNIT_LABELS_PLURAL[product.unit]}`
              : 'Precio unitario'
            }
          </Typography>
          <PriceDisplay
              basePrice={product.basePrice}
              priceBreaks={product.priceBreaks}
              hidePrice={product.hidePrice}
              unit={product.unit}
              isMobile={isMobile}
              hasMainPrice={false}
            />
            </Box>
          <Chip
            label={product.category}
            size="small"
            sx={{
              position: 'absolute',
              bottom: isMobile ? 10 : '0',
              right: 0,
              top: isMobile ? 'auto' : 0,
              margin: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
              fontWeight: 500,
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default FeaturedProductCard;
